import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Button,
  Avatar,
  Divider,
  Chip
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  PersonOutlined,
  EmailOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  AdminPanelSettingsOutlined,
  CloseOutlined,
  LanguageOutlined,
  WorkOutline,
  NotesOutlined
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Professional color palette matching the card component
const colors = {
  primary: '#0017E7',
  primaryLight: '#3347FF',
  primaryDark: '#0012B8',
  success: '#00C853',
  warning: '#FF9800',
  error: '#F44336',
  text: '#1A1A1A',
  textLight: '#616161',
  textMuted: '#9E9E9E',
  surface: '#FFFFFF',
  surfaceAlt: '#F5F7FA',
  border: '#E0E0E0',
  white: '#FFFFFF',
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    overflow: 'visible',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  color: colors.text,
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${colors.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: colors.surfaceAlt,
}));

const HeaderBar = styled(Box)(({ userType }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 4,
  background: userType === 'superadmin'
    ? `linear-gradient(90deg, ${colors.warning} 0%, #F57C00 100%)`
    : `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  borderRadius: '12px 12px 0 0',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 64,
  height: 64,
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  border: `3px solid ${colors.surface}`,
  fontSize: '1.5rem',
  fontWeight: 700,
  color: colors.white,
  boxShadow: '0 4px 12px rgba(0, 23, 231, 0.2)',
}));

const UserTypeChip = styled(Chip)(({ userType }) => ({
  height: 28,
  fontSize: '0.75rem',
  fontWeight: 700,
  borderRadius: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  background: userType === 'superadmin'
    ? `linear-gradient(135deg, ${colors.warning} 0%, #F57C00 100%)`
    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  color: colors.white,
  border: 'none',
  boxShadow: `0 2px 8px ${alpha(userType === 'superadmin' ? colors.warning : colors.primary, 0.3)}`,
}));

const InfoRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 0),
  '& .info-icon': {
    fontSize: 20,
    color: colors.textMuted,
    flexShrink: 0,
  }
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  minWidth: 120,
  color: colors.textLight,
  fontWeight: 600,
  fontSize: '0.875rem',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: colors.text,
  fontSize: '0.875rem',
  fontWeight: 400,
  wordBreak: 'break-word',
  flex: 1,
}));

const StatusChip = styled(Chip)(({ status }) => {
  const statusStyles = {
    active: {
      background: alpha(colors.success, 0.1),
      color: colors.success,
      border: `1px solid ${alpha(colors.success, 0.3)}`,
    },
    pending: {
      background: alpha(colors.warning, 0.1),
      color: colors.warning,
      border: `1px solid ${alpha(colors.warning, 0.3)}`,
    },
    inactive: {
      background: alpha(colors.error, 0.1),
      color: colors.error,
      border: `1px solid ${alpha(colors.error, 0.3)}`,
    }
  };

  return {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    ...statusStyles[status],
  };
});

const CloseButton = styled(Button)(({ theme }) => ({
  minWidth: 36,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 8,
  color: colors.textMuted,
  '&:hover': {
    background: alpha(colors.error, 0.08),
    color: colors.error,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  boxShadow: 'none',
  background: colors.primary,
  color: colors.white,
  '&:hover': {
    background: colors.primaryDark,
    boxShadow: `0 4px 12px ${alpha(colors.primary, 0.3)}`,
  }
}));

const ProfessionalDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  background: colors.border,
}));

const Header = ({ item, userType, t }) => {
  const initials = `${(item.first_name || '').slice(0, 1)}${(item.last_name || '').slice(0, 1)}`.toUpperCase() || 'U';

  return (
    <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 2 }}>
      <UserAvatar src={item.image}>
        {initials}
      </UserAvatar>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: colors.text,
          mb: 0.5,
          lineHeight: 1.3
        }}>
          {item.first_name} {item.last_name}
        </Typography>
        <UserTypeChip
          userType={userType}
          icon={userType === 'superadmin' ?
            <AdminPanelSettingsOutlined sx={{ fontSize: 14, color: colors.white + ' !important' }} /> :
            <PersonOutlined sx={{ fontSize: 14, color: colors.white + ' !important' }} />
          }
          label={userType === 'superadmin' ? t('superAdmin.adminDetailsDialog.superAdminLabel') : t('superAdmin.adminDetailsDialog.adminLabel')}
        />
      </Box>
    </Stack>
  );
};

const AdminDetailsDialog = ({ open, onClose, item, userType = 'admin' }) => {
  const { t } = useTranslation();

  if (!item) return null;

  const location = [item.city, item.postal_code, item.country].filter(Boolean).join(', ');

  const normalizeIsActive = (value) => {
    if (typeof value === 'string') {
      const v = value.toLowerCase();
      if (v === 'false' || v === '0') return false;
      return true;
    }
    if (typeof value === 'number') return value !== 0;
    if (value === false) return false;
    return true;
  };

  const getStatus = () => {
    if (normalizeIsActive(item.is_active) === false) return 'inactive';
    if (item.email_verified === false) return 'pending';
    return 'active';
  };

  const getStatusLabel = (status) => {
    return t(`superAdmin.adminDetailsDialog.statusLabels.${status}`);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <HeaderBar userType={userType} />

      <StyledDialogTitle>
        <span>{userType === 'superadmin' ? t('superAdmin.adminDetailsDialog.superAdminDetails') : t('superAdmin.adminDetailsDialog.adminDetails')}</span>
        <CloseButton onClick={onClose}>
          <CloseOutlined fontSize="small" />
        </CloseButton>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Header item={item} userType={userType} t={t} />

        <ProfessionalDivider />

        <Box>
          <InfoRow>
            <EmailOutlined className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.email')}</InfoLabel>
            <InfoValue>{item.email || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <PersonOutlined className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.username')}</InfoLabel>
            <InfoValue>{item.username || '-'}</InfoValue>
          </InfoRow>

          {userType !== 'superadmin' && (
            <InfoRow>
              <BusinessOutlined className="info-icon" />
              <InfoLabel>{t('superAdmin.adminDetailsDialog.company')}</InfoLabel>
              <InfoValue>{item.company || '-'}</InfoValue>
            </InfoRow>
          )}

          <InfoRow>
            <LocationOnOutlined className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.location')}</InfoLabel>
            <InfoValue>{location || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <LocationOnOutlined className="info-icon" sx={{ opacity: 0 }} />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.address')}</InfoLabel>
            <InfoValue>{item.address || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <LocationOnOutlined className="info-icon" sx={{ opacity: 0 }} />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.postalCode')}</InfoLabel>
            <InfoValue>{item.postal_code || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <WorkOutline className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.industry')}</InfoLabel>
            <InfoValue>{item.comp_industry || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <LanguageOutlined className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.website')}</InfoLabel>
            <InfoValue>
              {item.site_url ? (
                <a href={item.site_url.startsWith('http') ? item.site_url : `https://${item.site_url}`} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
                  {item.site_url}
                </a>
              ) : '-'}
            </InfoValue>
          </InfoRow>

          <InfoRow sx={{ alignItems: 'flex-start' }}>
            <NotesOutlined className="info-icon" sx={{ mt: 0.5 }} />
            <InfoLabel sx={{ mt: 0.5 }}>{t('superAdmin.adminDetailsDialog.description')}</InfoLabel>
            <InfoValue sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{item.comp_description || '-'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <AdminPanelSettingsOutlined className="info-icon" />
            <InfoLabel>{t('superAdmin.adminDetailsDialog.status')}</InfoLabel>
            <Box>
              <StatusChip
                label={getStatusLabel(getStatus())}
                status={getStatus()}
                size="small"
              />
            </Box>
          </InfoRow>

          {item.created_by && (
            <>
              <ProfessionalDivider />
              <Box sx={{
                background: colors.surfaceAlt,
                padding: 2,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                mt: 2
              }}>
                <Typography sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1
                }}>
                  {t('superAdmin.adminDetailsDialog.createdBy')}
                </Typography>
                <Typography sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: colors.text,
                  mb: 0.5
                }}>
                  {(item.created_by.first_name || '') + ' ' + (item.created_by.last_name || '')}
                </Typography>
                {item.created_by.email && (
                  <Typography sx={{
                    fontSize: '0.85rem',
                    color: colors.textLight
                  }}>
                    {item.created_by.email}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.border}`, background: colors.surfaceAlt }}>
        <ActionButton onClick={onClose} variant="contained">
          {t('superAdmin.adminDetailsDialog.close')}
        </ActionButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default AdminDetailsDialog;